/**
 * craft_feed.ts — NPC craft that should be HEARD (2026-09-20). PURE, globalThis-backed (the air_targets pattern).
 *
 * Other PLAYERS' craft are read straight off `atlas.remotes`. NPC craft — a versona crossing the sky to another town,
 * a cargo freighter and its escorts on the trade lane — are drawn by their own modules in their own coordinates; those
 * modules REPORT what they draw, each frame they draw it, and the audio runtime module voices the nearest few together
 * with the players' (lib/audio/passing_craft.ts). A report is a LEASE: a module that stops drawing a craft (it landed,
 * it was shot down, the module died) simply stops reporting and the engine fades — nothing to unregister, nothing to leak.
 *
 * THE WATER READS THE SAME FEED (2026-10-05, the water-interaction wave W3/W4): a craft low over a lake or the sea
 * raises spray and rings (`AtlasWaterModule.stepCraftWater` walks `forEachCraft`). A report may carry its hull length
 * (`hullM`) and be SILENT — drawn and felt by the water, but never voiced as a passing engine (a remote player's hull is
 * voiced from `atlas.remotes` already; a war hull carries its own sounds). `liveCraft` — the audio's view — never
 * returns a silent report, so the passing-craft mix is exactly what it was.
 */
import type { CraftKind } from './passing_craft.js';

export interface CraftReport { id: string; kind: CraftKind; x: number; y: number; z: number; atMs: number; hullM: number; silent: boolean; ownSpray: boolean }
export const CRAFT_LEASE_MS = 700;

/** Optional facts a report may carry for the water (hull length; silent = not voiced; ownSpray = draws its own spray). */
export interface CraftReportOpts { hullM?: number; silent?: boolean; ownSpray?: boolean }

interface Feed { craft: Map<string, CraftReport> }
const holder = globalThis as unknown as { __atlasCraftFeed?: Feed };
const feed = (): Feed => (holder.__atlasCraftFeed ??= { craft: new Map() });

/** "I am drawing this craft HERE, now." ABSOLUTE world coordinates (the ABSOLUTE-IN law). */
export function reportCraft(id: string, kind: CraftKind, x: number, y: number, z: number, nowMs: number, opts?: CraftReportOpts): void {
  const f = feed(), r = f.craft.get(id);
  const hullM = opts?.hullM ?? 0, silent = opts?.silent === true, ownSpray = opts?.ownSpray === true;
  if (r) { r.kind = kind; r.x = x; r.y = y; r.z = z; r.atMs = nowMs; r.hullM = hullM; r.silent = silent; r.ownSpray = ownSpray; }
  else f.craft.set(id, { id, kind, x, y, z, atMs: nowMs, hullM, silent, ownSpray });
}

/** Every AUDIBLE craft reported within the lease (expired ones are dropped as they are found). */
export function liveCraft(nowMs: number): CraftReport[] {
  const out: CraftReport[] = [];
  const f = feed();
  for (const [id, r] of f.craft) { if (nowMs - r.atMs <= CRAFT_LEASE_MS) { if (!r.silent) out.push(r); } else f.craft.delete(id); }
  return out;
}

/** Visit every craft reported within the lease — silent ones included — without allocating (the water's per-frame walk). */
export function forEachCraft(nowMs: number, fn: (r: CraftReport) => void): void {
  const f = feed();
  for (const [id, r] of f.craft) { if (nowMs - r.atMs <= CRAFT_LEASE_MS) fn(r); else f.craft.delete(id); }
}
